import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import { SearchNormal1 } from 'iconsax-react';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { diApi } from './api';
import type { InboundDocumentRow, InboundDocumentStatus, InboundDocumentsStatus } from './types';
import DocumentDetailDrawer from './components/DocumentDetailDrawer';
import { CATEGORY_LABELS, DOCUMENT_STATUS_LABELS, DocumentStatusChip } from './components/documentLabels';
import { errorText, formatCount, relativeTime } from './components/format';

type StatusFilter = 'ALL' | InboundDocumentStatus;
const FILTERS: StatusFilter[] = ['ALL', 'NEEDS_REVIEW', 'INCOMPLETE', 'COMPLETE', 'PROCESSING'];

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12 };
const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  clientUid: string;
}

/**
 * Data Integrations → Documents: one row per load built from the files the client sent
 * over SFTP. Read-only view of what the prod Lambda (lambdas/sftp-documents) wrote.
 */
export default function Documents({ clientUid }: Props) {
  const { showToast } = useApi();
  const toastRef = useRef(showToast);
  toastRef.current = showToast;

  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [rows, setRows] = useState<InboundDocumentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<InboundDocumentsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openPro, setOpenPro] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const requestId = useRef(0);

  // Search fires once typing pauses, and always from the first page.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(
    (silent = false) => {
      const ticket = ++requestId.current;
      if (!silent) setLoading(true);
      setError(null);
      // The counts are a nicety: if they fail, the list still shows.
      diApi
        .getDocumentsStatus(clientUid)
        .then((st) => {
          if (ticket === requestId.current && st.success !== false) setSummary(st.data);
        })
        .catch(() => undefined);
      diApi
        .getDocuments(clientUid, { search: query || undefined, status: status === 'ALL' ? undefined : status, page: page + 1, limit })
        .then((list) => {
          if (ticket !== requestId.current) return;
          if (list.success === false) {
            setError(list.message || 'Could not load documents.');
            return;
          }
          setRows(list.data?.rows ?? []);
          setTotal(list.data?.total ?? 0);
        })
        .catch((err) => {
          if (ticket === requestId.current) setError(errorText(err, 'Could not load documents.'));
        })
        .finally(() => {
          if (ticket === requestId.current) setLoading(false);
        });
    },
    [clientUid, query, status, page, limit]
  );

  useEffect(() => {
    load();
  }, [load]);

  // A delayed refresh must use the filters current when it fires, not when it was scheduled.
  const loadRef = useRef(load);
  loadRef.current = load;
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    },
    []
  );

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await diApi.requestDocumentsScan(clientUid);
      if (res.success === false) {
        toastRef.current(res.message || 'Could not start the check', 'error');
        return;
      }
      toastRef.current('Checking for new files. New loads appear here within a few minutes.', 'success');
      // The Lambda registers new files within seconds; reading them takes longer.
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => loadRef.current(true), 20_000);
    } catch (err) {
      toastRef.current(errorText(err, 'Could not start the check'), 'error');
    } finally {
      setChecking(false);
    }
  };

  const counts = summary?.loads ?? {};
  const totalLoads = Object.values(counts).reduce((a, b) => a + (b ?? 0), 0);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={2} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5">Documents</Typography>
          <Typography variant="body2" color="text.secondary">
            One row per load, built from every file the client sent over SFTP.
            {summary?.lastFileAt ? ` Last file ${relativeTime(summary.lastFileAt)}.` : ''}
          </Typography>
        </Box>
        {summary?.canRequest && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={checkNow}
            disabled={checking}
            startIcon={checking ? <CircularProgress size={14} /> : undefined}
          >
            Check for new files
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Load, reference, vendor, seal, plate or driver"
          sx={{ minWidth: 320, flex: '1 1 320px', maxWidth: 480 }}
          inputProps={{ 'aria-label': 'Search documents' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchNormal1 size={16} />
              </InputAdornment>
            )
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={status}
          onChange={(_e, v: StatusFilter | null) => {
            if (!v) return;
            setStatus(v);
            setPage(0);
          }}
          aria-label="Filter loads by status"
        >
          {FILTERS.map((f) => (
            <ToggleButton key={f} value={f} sx={{ textTransform: 'none', px: 1.5 }}>
              {f === 'ALL'
                ? `All${totalLoads ? ` · ${formatCount(totalLoads)}` : ''}`
                : `${DOCUMENT_STATUS_LABELS[f]}${counts[f] ? ` · ${formatCount(counts[f])}` : ''}`}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <MainCard content={false}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Load</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Truck / trailer</TableCell>
                <TableCell>Drivers</TableCell>
                <TableCell>Seal</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last file</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const s = r.summary || {};
                const drivers = [s.driver1?.name, s.driver2?.name].filter(Boolean);
                return (
                  <TableRow
                    key={r.uid}
                    hover
                    tabIndex={0}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpenPro(r.pro)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenPro(r.pro);
                      }
                    }}
                  >
                    <TableCell>
                      <Box component="code" sx={mono}>
                        {r.pro}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ...mono, fontSize: 11 }}>
                        {r.ref}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{s.vendorName || '—'}</Typography>
                      {(s.mcNumber || s.dotNumber) && (
                        <Typography variant="caption" color="text.secondary">
                          {[s.mcNumber && `MC ${s.mcNumber}`, s.dotNumber && `DOT ${s.dotNumber}`].filter(Boolean).join(' · ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={mono}>
                        {s.truck?.licensePlate || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={mono}>
                        {s.trailer?.licensePlate || s.trailer?.number || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{drivers.length ? drivers.join(', ') : '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={mono}>
                        {s.sealNumber || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {r.fileCount} file{r.fileCount === 1 ? '' : 's'}
                      </Typography>
                      {r.documentsMissing.length > 0 && (
                        <Tooltip title={`Missing: ${r.documentsMissing.map((c) => CATEGORY_LABELS[c] ?? c).join(', ')}`}>
                          <Typography variant="caption" color="warning.dark">
                            {r.documentsMissing.length} missing
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <DocumentStatusChip status={r.documentStatus} />
                        {r.flagCount > 0 && (
                          <Chip size="small" variant="outlined" label={`${r.flagCount} flag${r.flagCount === 1 ? '' : 's'}`} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        {relativeTime(r.lastFileAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && rows.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      {query || status !== 'ALL'
                        ? 'No loads match these filters.'
                        : 'No documents yet. Loads appear here as soon as files arrive on the SFTP server.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} aria-label="Loading documents" />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={limit}
          rowsPerPageOptions={[25, 50, 100]}
          onRowsPerPageChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(0);
          }}
        />
      </MainCard>

      <DocumentDetailDrawer clientUid={clientUid} pro={openPro} canRequest={!!summary?.canRequest} onClose={() => setOpenPro(null)} />
    </Stack>
  );
}
